import * as path from 'node:path';
import { getBridgeRoot } from '../../../files/cwd/index.js';
import { cliSimpleGit } from '../../cli-simple-git.js';
import { isGitRepoDirectory } from '../../is-git-repo.js';
import { hydrateUnifiedPatchWithFileContext } from '../patch/hydrate/index.js';
import { unifiedDiffForFile, unifiedDiffForFileInRange } from '../patch/unified-diff-for-file.js';
import { parentForCommitDiff } from '../listing/parent-for-commit-diff.js';
import { filePathRelativeToRepoRootFromWorkspace } from '../listing/file-path-relative-to-repo-root-from-workspace.js';
import { normalizeRepoPathRelativeToWorkspaceRoot } from './normalize-repo-path-relative-to-workspace-root.js';
import {
  resolveCommitFileTotalLines,
  resolveWorkingTreeFileTotalLines,
} from '../total-lines/resolve-changed-file-total-lines.js';
import type { WorkingTreeChangeKind } from '../types.js';
import type { WorkingTreeRepoChangesBasis } from './types.js';

export async function getWorkingTreeChangeFilePatch(options: {
  repoGitCwd: string;
  /** Repo path relative to the bridge or session/preview workspace root. */
  repoPathRelativeToWorkspaceRoot: string;
  /** File path relative to the bridge or session/preview workspace root. */
  filePathRelativeToWorkspaceRoot: string;
  change: WorkingTreeChangeKind;
  movedFromFilePathRelativeToWorkspaceRoot?: string | null;
  basis: WorkingTreeRepoChangesBasis;
}): Promise<{ patchContent?: string; totalLines: number | null }> {
  const repoGitCwd = path.resolve(options.repoGitCwd);
  if (!(await isGitRepoDirectory(repoGitCwd))) {
    throw new Error('Not a git repository');
  }

  const repoPathRelativeToWorkspaceRoot = normalizeRepoPathRelativeToWorkspaceRoot(
    options.repoPathRelativeToWorkspaceRoot,
  );
  const filePathRelativeToRepoRoot = filePathRelativeToRepoRootFromWorkspace(
    options.filePathRelativeToWorkspaceRoot,
    repoPathRelativeToWorkspaceRoot,
  );
  const movedFromFilePathRelativeToRepoRoot =
    options.change === 'moved' && options.movedFromFilePathRelativeToWorkspaceRoot
      ? filePathRelativeToRepoRootFromWorkspace(
          options.movedFromFilePathRelativeToWorkspaceRoot,
          repoPathRelativeToWorkspaceRoot,
        )
      : undefined;

  if (options.basis.kind === 'commit') {
    const commitSha = options.basis.sha.trim();
    if (!commitSha) throw new Error('commit sha is required for commit file patch');
    const g = cliSimpleGit(repoGitCwd);
    const parentSha = await parentForCommitDiff(g, commitSha);
    const range = `${parentSha}..${commitSha}`;
    const patchContent = await unifiedDiffForFileInRange(
      repoGitCwd,
      range,
      filePathRelativeToRepoRoot,
      options.change,
      movedFromFilePathRelativeToRepoRoot,
    );
    const isBinary = Boolean(patchContent?.includes('Binary files'));
    const totalLines = await resolveCommitFileTotalLines({
      repoGitCwd,
      pathInRepo: filePathRelativeToRepoRoot,
      change: options.change,
      commitSha,
      parentSha,
      isBinary,
    });
    return { patchContent, totalLines };
  }

  const filePath = path.join(repoGitCwd, filePathRelativeToRepoRoot);
  const hydrateKind = options.change === 'moved' ? 'modified' : options.change;
  let patchContent = await unifiedDiffForFile(
    repoGitCwd,
    filePathRelativeToRepoRoot,
    options.change,
    movedFromFilePathRelativeToRepoRoot,
  );
  if (patchContent) {
    patchContent = await hydrateUnifiedPatchWithFileContext(
      patchContent,
      filePath,
      repoGitCwd,
      filePathRelativeToRepoRoot,
      hydrateKind,
    );
  }
  const totalLines = await resolveWorkingTreeFileTotalLines({
    repoGitCwd,
    pathInRepo: filePathRelativeToRepoRoot,
    change: options.change,
    isBinary: Boolean(patchContent?.includes('Binary files')),
  });
  return { patchContent, totalLines };
}
